/** Browser/WASM binding shared by Flux Agent capabilities; all policy lives in Rust.
 * @module apps/web/src/lib/fluxAgentBrowser
 */
/** Rust WASM exports consumed by this binding; one module is shared across frames. */
export interface FluxAgentBrowser {
  /** Initialize the generated wasm-bindgen module. */
  default: (input?: unknown) => Promise<unknown>;
  /** Serialize the sandbox document. */
  view_document: (url: string, payload: string, css: string) => string;
  /** Validate an incoming message. */
  view_message: (raw: string, origin: string, sameSource: boolean, token: string) => string;
  /** Decide liveness from DOM observations. */
  view_watchdog: (state: string) => string;
  /** Pure photo selection/sizing policy. */
  photo_policy: (raw: string) => string;
  /** Validate saved Kanban settings and project current task-store data locally. */
  kanban_project: (raw: string) => string;

  /** Account-scoped recovery pause policy. */
  session_recovery: (raw: string, owner: string, session: string) => string;
  /** Account-scoped opaque form checkpoint. */
  session_checkpoint: (raw: string, owner: string, session: string) => string;
  /** Allocate one frame's bounded message counter. */
  ViewBudget: new () => {
    /** Pass monotonic milliseconds to Rust; false means the message budget is exhausted. */
    accept: (now: number) => boolean;
    /** Release the per-frame WASM allocation on unmount. */
    free: () => void;
  };
}

/** Build-injected hash of the paired JS/WASM artifacts; absent only in isolated test harnesses. */
declare const __FLUX_AGENT_BROWSER_VERSION__: string;

/** Cached module and initialization, never reinstantiated for individual frames. */
let protocol: FluxAgentBrowser | null = null;
/** Shared initialization promise; reset after failure so a later open may retry. */
let pending: Promise<void> | null = null;

/** Binding: fetch the same-origin module and WASM on demand, off the initial page-render path. */
async function browserModule(): Promise<FluxAgentBrowser> {
  const version =
    typeof __FLUX_AGENT_BROWSER_VERSION__ === 'string' ? __FLUX_AGENT_BROWSER_VERSION__ : '';
  const suffix = version ? `?v=${encodeURIComponent(version)}` : '';
  const url = new URL(
    `/flux-agent-browser/fluxability_flux_agent_browser.js${suffix}`,
    location.origin
  ).href;
  const module = (await import(/* @vite-ignore */ url)) as FluxAgentBrowser;
  await module.default({
    module_or_path: new URL(
      `/flux-agent-browser/fluxability_flux_agent_browser_bg.wasm${suffix}`,
      location.origin
    ).href,
  });
  return module;
}

/** Binding: initialize once; a supplied loader lets non-browser tests read the exact built WASM. */
export function loadFluxAgentBrowser(
  loader: () => Promise<FluxAgentBrowser> = browserModule
): Promise<void> {
  pending ??= loader()
    .then(module => {
      protocol = module;
    })
    .catch(error => {
      pending = null;
      throw error;
    });
  return pending;
}

/** Binding: expose module readiness to the rendering component. */
export function fluxAgentBrowserReady(): boolean {
  return protocol !== null;
}

/** Binding: fail closed if the component calls Rust before initialization completes. */
export function fluxAgentBrowserCore(): FluxAgentBrowser {
  if (!protocol) throw new Error('The view runtime is not ready.');
  return protocol;
}

/** Browser photo observations; decoding and file bytes stay in the caller. */
export interface PhotoPolicyInput {
  /** Decoded image width in pixels, when known. */
  width?: number;
  /** Decoded image height in pixels, when known. */
  height?: number;
  /** Maximum permitted output side in pixels. */
  maxSide?: number;
  /** Original filename used for format recognition. */
  name?: string;
  /** Browser-reported media type. */
  mimeType?: string;
  /** Original file size in bytes. */
  size?: number;
}
/** Rust-derived image fit and picker result. */
export interface PhotoPolicyResult {
  /** Output image width in pixels. */
  width: number;
  /** Output image height in pixels. */
  height: number;
  /** Whether Rust accepts this file for the image picker. */
  pickable: boolean;
}
/** Binding: load the shared Rust module once and serialize photo observations. */
export async function photoPolicy(input: PhotoPolicyInput): Promise<PhotoPolicyResult> {
  await loadFluxAgentBrowser();
  return JSON.parse(fluxAgentBrowserCore().photo_policy(JSON.stringify(input)));
}
